import Vue from 'vue'
import { PopupManager } from './popup'

const PopperJS = Vue.prototype.$isServer ? function() {} : require('./popper')
const stop = e => e.stopPropagation()

/**
 * @param {HTMLElement} [reference=$refs.reference] - Ссылочный элемент, используемый для позиционирования кнопки.
 * @param {HTMLElement} [popper=$refs.popper] - Элемент HTML, используемый как popper, или конфигурация, используемая для создания popper.
 * @param {Boolean} [visible=false] Видимость всплывающего элемента.
 */
export default {
  props: {
    /**
     * @ignore
     */
    transformOrigin: {
      type: [Boolean, String],
      default: true
    },
    /**
     * Размещение допустимых значений подсказки
     */
    placement: {
      type: String,
      default: 'bottom',
      validator: function (value) {
        return [
          'top', 'top-start', 'top-end',
          'bottom', 'bottom-start', 'bottom-end',
          'left', 'left-start', 'left-end',
          'right', 'right-start', 'right-end',
          'center', 'top-center', 'bottom-center', 'left-center', 'right-center'
        ].indexOf(value) !== -1
      },
    },
    /**
     * @ignore
     */
    boundariesPadding: {
      type: Number,
      default: 5
    },
    /**
     * @ignore
     */
    reference: {},
    /**
     * @ignore
     */
    popper: {},
    /**
     * Количество пикселей, на которое поппер будет сдвинут (может быть отрицательным).
     */
    offset: {
      default: 0
    },
    /**
     * Видимость всплывающей подсказки
     */
    value: Boolean,
    /**
     * @ignore
     */
    appendToBody: {
      type: Boolean,
      default: true
    },
    /**
     * параметры popper.js
     */
    popperOptions: {
      type: Object,
      default() {
        return {
          gpuAcceleration: false
        }
      }
    }
  },
  data() {
    return {
      showPopper: false,
      currentPlacement: ''
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.showPopper = val
        this.$emit('input', val)
      }
    },
    showPopper: {
      immediate: true,
      handler(val) {
        if (this.disabled) { return }
        val ? this.updatePopper() : this.destroyPopper()
        this.$emit('input', val)
      }
    }
  },
  methods: {
    _resolveEffectivePlacement(placement) {
      const map = {
        'center': 'bottom',
        'top-center': 'top',
        'bottom-center': 'bottom',
        'left-center': 'left',
        'right-center': 'right'
      }
      return map[placement] || placement
    },
    createPopper() {
      if (this.$isServer) { return }
      this.currentPlacement = this.currentPlacement || this.placement
      const effectivePlacement = this._resolveEffectivePlacement(this.currentPlacement)
      if (!/^(top|bottom|left|right)(-start|-end)?$/g.test(effectivePlacement)) {
        return
      }
      const options = this.popperOptions
      const popper = this.popperElm = this.popperElm || this.popper || this.$refs.popper
      let reference = this.referenceElm = this.referenceElm || this.reference || this.$refs.reference

      if (!reference &&
        this.$slots.reference &&
        this.$slots.reference[0]) {
        reference = this.referenceElm = this.$slots.reference[0].elm
      }
      if (!popper || !reference) { return }
      if (this.appendToBody) { document.body.appendChild(this.popperElm) }
      if (this.popperJS && this.popperJS.destroy) {
        this.popperJS.destroy()
      }
      options.placement = effectivePlacement
      options.offset = this.offset
      if (this.currentPlacement === 'center') {
        const defaultModifiers = ['shift', 'offset', 'preventOverflow', 'keepTogether', 'flip', 'applyStyle']
        const base = Array.isArray(options.modifiers) && options.modifiers.length ? options.modifiers : defaultModifiers
        const applyStyleIdx = base.indexOf('applyStyle')
        const insertAt = applyStyleIdx >= 0 ? applyStyleIdx : base.length
        options.modifiers = [
          ...base.slice(0, insertAt),
          function centerPopperModifier(data) {
            const ref = data.offsets.reference
            const pop = data.offsets.popper
            const vw = window.innerWidth
            const vh = window.innerHeight
            let left = ref.left + (ref.width - pop.width) / 2
            let top = ref.top + (ref.height - pop.height) / 2
            left = Math.max(5, Math.min(left, vw - pop.width - 5))
            top = Math.max(5, Math.min(top, vh - pop.height - 5))
            data.offsets.popper = { ...pop, left, top }
            return data
          },
          ...base.slice(insertAt)
        ]
      }
      this.popperJS = new PopperJS(reference, popper, options)
      this.popperJS.onCreate(() => {
        this.$emit('created', this)
        this.resetTransformOrigin()
        this.$nextTick(this.updatePopper)
      })
      if (typeof options.onUpdate === 'function') {
        this.popperJS.onUpdate(options.onUpdate)
      }
      this.popperJS._popper.style.zIndex = PopupManager.nextZIndex()
      this.popperElm.addEventListener('click', stop)
      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObserver = new ResizeObserver(() => { this.updatePopper() })
        this._resizeObserver.observe(this.popperElm)
      }
    },
    updatePopper() {
      const popperJS = this.popperJS
      if (popperJS) {
        popperJS.update()
        if (popperJS._popper) {
          popperJS._popper.style.zIndex = PopupManager.nextZIndex()
        }
      } else {
        this.$nextTick(() => {
          this.createPopper()
        })
      }
    },
    doDestroy(forceDestroy) {
      /* istanbul ignore if */
      if (this._resizeObserver) {
        this._resizeObserver.disconnect()
        this._resizeObserver = null
      }
      if (!this.popperJS || (this.showPopper && !forceDestroy)) { return }
      this.popperJS.destroy()
      this.popperJS = null
    },
    destroyPopper() {
      if (this.popperJS) {
        this.resetTransformOrigin()
      }
    },
    resetTransformOrigin() {
      if (!this.transformOrigin) { return }
      let placementMap = {
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left'
      }
      let placement = this.popperJS._popper.getAttribute('x-placement').split('-')[0]
      let origin = placementMap[placement]
      this.popperJS._popper.style.transformOrigin = typeof this.transformOrigin === 'string'
        ? this.transformOrigin
        : ['top', 'bottom'].indexOf(placement) > -1 ? `center ${ origin }` : `${ origin } center`
    },
  },
  beforeDestroy() {
    this.doDestroy(true)
    if (this.popperElm && this.popperElm.parentNode === document.body) {
      this.popperElm.removeEventListener('click', stop)
      document.body.removeChild(this.popperElm)
    }
  },
  deactivated() {
    this.$options.beforeDestroy[0].call(this)
  }
}